/**
 * Метаданные музыкальных моделей (Suno / Mureka) для отображения:
 * mini-логотип + читаемое имя + версия. `track.model` хранит id вида
 * `mureka-8` / `suno-v4_5all` — здесь маппим его в человекочитаемый вид.
 *
 * Имена держим 1:1 с селектором генератора (useProviders MODEL_DISPLAY), но
 * тут несём ДРУГИЕ поля — провайдера, иконку и версию — которых там нет.
 * `suno-lyrics` и пустой model → null (не аудио-модель / старый трек).
 */

export interface MusicModelInfo {
	/** Нейросеть-провайдер. */
	provider: 'suno' | 'mureka'
	/** Имя нейросети: 'Suno' | 'Mureka'. */
	providerName: string
	/** Полное имя модели: 'Mureka 8', 'Suno V4.5 All'. */
	name: string
	/** Версия без имени провайдера: '8', 'V4.5 All'. */
	version: string
	/** Путь к mini-логотипу (monochrome — ModelIcon инвертит на тёмной теме). */
	icon: string
}

const SUNO_ICON = '/icons/suno.svg'
const MUREKA_ICON = '/icons/mureka.svg'

/** Известные модели: id → читаемые имя/версия. Совпадает с именами в
 *  селекторе генератора. Неизвестные id парсятся фолбэком ниже. */
const KNOWN: Record<string, { name: string; version: string }> = {
	'suno-v4_5all': { name: 'Suno V4.5 All', version: 'V4.5 All' },
	'suno-v5': { name: 'Suno V5', version: 'V5' },
	'suno-v5_5': { name: 'Suno V5.5', version: 'V5.5' },
	'suno-v6': { name: 'Suno V6', version: 'V6' },
	'suno-v6_wild': { name: 'Suno V6 Wild', version: 'V6 Wild' },
	'suno-v6_mini': { name: 'Suno V6 Mini', version: 'V6 Mini' },
	'mureka-8': { name: 'Mureka 8', version: '8' },
	'mureka-9': { name: 'Mureka 9', version: '9' },
	'mureka-9.5': { name: 'Mureka 9.5', version: '9.5' },
	'mureka-7.6': { name: 'Mureka 7.6', version: '7.6' },
	'mureka-o2': { name: 'Mureka o2', version: 'o2' },
}

/** Разобрать `track.model` в данные для отображения. null — нет осмысленной
 *  музыкальной модели (пустой / lyrics / неизвестный провайдер). */
export function describeMusicModel(model: string | null | undefined): MusicModelInfo | null {
	const raw = (model || '').trim()
	if (!raw) return null
	const lower = raw.toLowerCase()
	// Lyrics-операции (suno-lyrics) — это не аудио-модель, не метим.
	if (lower.endsWith('-lyrics')) return null
	const provider: 'suno' | 'mureka' | null = lower.startsWith('mureka')
		? 'mureka'
		: lower.startsWith('suno')
			? 'suno'
			: null
	if (!provider) return null
	const providerName = provider === 'mureka' ? 'Mureka' : 'Suno'
	const icon = provider === 'mureka' ? MUREKA_ICON : SUNO_ICON
	const known = KNOWN[lower]
	if (known) return { provider, providerName, name: known.name, version: known.version, icon }
	// Фолбэк для будущих/неизвестных id: версия из суффикса после провайдера
	// ('mureka-X' → 'X'; подчёркивания → точки для читаемости).
	const version = raw.slice(provider.length).replace(/^[-_]/, '').replace(/_/g, '.')
	const name = version ? `${providerName} ${version}` : providerName
	return { provider, providerName, name, version, icon }
}
